import { Controller } from 'stimulus'
import { useDispatch } from 'stimulus-use'
import { jwtDecode } from 'jwt-decode'

import { fetchCurrencies } from '../base/plans'
import Observable from '../utils/observable'
import SnackBar from '../../_components/snackbar/snackbar'
import FormController from '../../_components/form/form_controller'

const CONTROLLER_IDENTIFIER = 'currency-controller'
const CURRENCY_TOKEN_KEY = 'currency-token'

const getValidCurrencyToken = () => {
  /**
   * We allow a token as URL parameter that will override the currencies
   * set by the location API
   */
  const searchParams = new URLSearchParams(document.location.search)
  const token = searchParams.get('token')

  if (token) {
    localStorage.setItem(CURRENCY_TOKEN_KEY, JSON.stringify(token))
    searchParams.delete('token')
    document.location.search = searchParams.size === 0 ? '' : searchParams.toString()
  }

  try {
    const currencyToken = JSON.parse(localStorage.getItem(CURRENCY_TOKEN_KEY)) || undefined
    const validToken = currencyToken ? jwtDecode(currencyToken)?.exp > Date.now() / 1000 : false

    if (validToken) {
      return currencyToken
    }
  } catch (e) {
    // If the token is invalid or expired, we will just ignore it
  }

  localStorage.removeItem(CURRENCY_TOKEN_KEY)
  return undefined
}

export const getCurrencyController = (application) => {
  const controllerElement = document.querySelector(`.${CONTROLLER_IDENTIFIER}`)

  if (!controllerElement) {
    return null
  }

  const currencyController = application.getControllerForElementAndIdentifier(controllerElement, 'currency')
  return currencyController
}

export default class Currency extends Controller {
  _defaultSelectedCurrency = null

  _outsideSetCurrency = null

  availableCurrencies = []

  observable = new Observable()

  async connect() {
    this.element.classList.add(CONTROLLER_IDENTIFIER)

    useDispatch(this)

    const token = getValidCurrencyToken()
    const { data, errors } = await fetchCurrencies(token)

    if (data) {
      this.availableCurrencies = data.currencies.available
      this._defaultSelectedCurrency = data.currencies.default

      this.propagateSetCurrency(true)
    }

    if (errors.length > 0) {
      // eslint-disable-next-line no-new
      new SnackBar('Something went wrong when setting the currency, please try again later', {
        modifiers: 'error',
        autoHide: true,
        duration: FormController.ALERT_DURATION,
        position: 'top',
      })
    }
  }

  get selectedCurrency() {
    return this._outsideSetCurrency || this._defaultSelectedCurrency
  }

  propagateSetCurrency(initialSet) {
    const state = { currency: this.selectedCurrency, availableCurrencies: this.availableCurrencies, initialSet }

    this.observable.setState(state)
    this.dispatch('setCurrency', state)
  }

  setCurrency(currency) {
    const safeguardedCurrency = String(currency).toUpperCase()
    this._outsideSetCurrency = safeguardedCurrency

    if (this.availableCurrencies.length > 0) {
      const newCurrency = this.availableCurrencies.find((_currency) => _currency === safeguardedCurrency)

      if (newCurrency) {
        this.propagateSetCurrency(false)
      } else {
        this._outsideSetCurrency = null
      }
    }
  }

  getObservable() {
    return this.observable
  }
}
